import React, { useCallback, useEffect, useRef, useState } from 'react';
import RootUpdateBanner from './RootUpdateBanner';
import RootDashboardActivity from './RootDashboardActivity';
import RootSessionOverlay from './RootSessionOverlay';
import { clearSecureSession, rootApi } from './RootScurityClient';

const HEARTBEAT_MS = 2500;
const CHANNEL_NAME = 'ourzest-secure-session';

function removeAuthenticationState() {
  [
    'user', 'user_role', 'auth_timestamp', 'current_shop', 'session_id',
    'is_super_admin', 'user_subscription',
  ].forEach((key) => localStorage.removeItem(key));
}

function noticeFor(detail = {}) {
  if (detail.code === 'SESSION_REPLACED' || detail.reason === 'new_device_login') {
    return {
      title: 'LOGGED INTO ANOTHER DEVICE',
      message: 'This username was used to sign in on another device. This device is being logged out for your security.',
      code: 'SESSION_REPLACED',
    };
  }
  if (['IP_BLOCKED', 'IP_NOT_APPROVED'].includes(detail.code)) {
    return {
      title: 'IP ACCESS REVOKED',
      message: detail.message || detail.error || 'This network is no longer approved for your username.',
      code: detail.code,
    };
  }
  return {
    title: 'SESSION ENDED',
    message: detail.message || detail.error || 'Your session is no longer active. Please sign in again.',
    code: detail.code || 'SESSION_REVOKED',
  };
}

/**
 * Mount ONCE, directly inside a top-level flex/column wrapper that sits
 * ABOVE `.oz-dashboard-shell` — never as a child of the shell's CSS grid.
 * See DashboardBusinessManager.js for the required wrapper markup.
 */
export default function RootRuntimeBridge({
  applicationId = null,
  clientVersion = 'web',
  onLogout,
  loginPath = '/',
}) {
  const [notice, setNotice] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const channelRef = useRef(null);
  const finishingRef = useRef(false);
  const cacheEpochRef = useRef(null);

  const receiveSessionEnd = useCallback((detail, broadcast = true) => {
    const next = noticeFor(detail);
    setNotice((current) => current || next);
    setCountdown(3);
    if (broadcast) {
      try { channelRef.current?.postMessage({ type: 'session-ended', detail: next }); } catch {}
      try { localStorage.setItem('ourzest_session_event', JSON.stringify({ ...next, at: Date.now() })); } catch {}
    }
  }, []);

  const finishLogout = useCallback(() => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    clearSecureSession(true);
    removeAuthenticationState();
    try { onLogout?.(); } catch {}
    window.location.replace(loginPath);
  }, [loginPath, onLogout]);

  useEffect(() => {
    const ended = (event) => receiveSessionEnd(event.detail || {}, true);
    const storage = (event) => {
      if (event.key !== 'ourzest_session_event' || !event.newValue) return;
      try { receiveSessionEnd(JSON.parse(event.newValue), false); } catch {}
    };
    window.addEventListener('ourzest:session-ended', ended);
    window.addEventListener('storage', storage);
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;
      channel.onmessage = (event) => {
        if (event.data?.type === 'session-ended') receiveSessionEnd(event.data.detail, false);
      };
    }
    return () => {
      window.removeEventListener('ourzest:session-ended', ended);
      window.removeEventListener('storage', storage);
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [receiveSessionEnd]);

  useEffect(() => {
    let alive = true;
    const heartbeat = async () => {
      if (!alive || notice) return;
      try {
        const result = await rootApi('root_session_api.php?action=heartbeat');
        const nextCacheEpoch = Number(result?.session?.cache_epoch || 0);
        if (nextCacheEpoch) {
          if (cacheEpochRef.current !== null && cacheEpochRef.current !== nextCacheEpoch) {
            window.dispatchEvent(new CustomEvent('ourzest:cache-cleared', {
              detail: { cache_epoch: nextCacheEpoch, source: 'server_access_change' },
            }));
          }
          cacheEpochRef.current = nextCacheEpoch;
        }
      }
      catch (error) {
        // rootApi emits ourzest:session-ended with the authoritative reason.
        if (![401, 403].includes(error?.status)) console.error('Session heartbeat failed:', error);
      }
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, HEARTBEAT_MS);
    const wake = () => { if (document.visibilityState === 'visible') heartbeat(); };
    window.addEventListener('focus', wake);
    document.addEventListener('visibilitychange', wake);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', wake);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [notice]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setInterval(() => {
      setCountdown((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          finishLogout();
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finishLogout, notice]);

  return (
    <>
      <RootUpdateBanner applicationId={applicationId} clientVersion={clientVersion} />
      <RootDashboardActivity />
      <RootSessionOverlay notice={notice} countdown={countdown} />
    </>
  );
}